import { Action, Middleware, configureStore } from "@reduxjs/toolkit";
import { createStateSyncMiddleware, initMessageListener } from "redux-state-sync";
import { ThunkAction } from "redux-thunk";

import { setChannel } from "./features/channel/channelSlice";
import rootReducer, { RootState } from "./rootReducer";

/**
 * Middleware that logs a warning in dev mode when deprecated Redux map actions
 * are dispatched. Helps catch stale code paths that should use Zustand instead.
 */
const deprecatedMapActionWarning: Middleware = () => (next) => (action) => {
  if (
    process.env.NODE_ENV !== "production" &&
    typeof action === "object" &&
    action !== null &&
    "type" in action &&
    typeof (action as { type: string }).type === "string" &&
    (action as { type: string }).type.startsWith("map/")
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Deprecated Redux] Action dispatched: ${(action as { type: string }).type}. ` +
        `Use Zustand \`useMapStore\` instead. Removal deadline: 2026-04-30`
    );
  }
  return next(action);
};

// Actions excluded from the redux-state-sync cross-tab broadcast. Filter
// setFilterId actions must stay OUT of this list: chart popouts follow the
// main tab's filter only via that broadcast (EVA-69).
export const stateSyncBlacklist = ["map/setLayers"] as const;

export default function initStore({
  channel,
  preloadedState
}: {
  channel: string;
  preloadedState?: Partial<RootState>;
}) {
  const config = {
    channel,
    blacklist: stateSyncBlacklist
  };
  const middlewares = createStateSyncMiddleware(config);
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false
      }).concat(middlewares, deprecatedMapActionWarning),
    devTools: true
  });

  initMessageListener(store);

  store.dispatch(setChannel(channel));
  return store;
}

export type AppThunk = ThunkAction<void, RootState, unknown, Action<string>>;
export type AppStore = ReturnType<typeof initStore>;
export type AppDispatch = AppStore["dispatch"];
